import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { handleTokenAndPostRequest, trackEvent } from "../utils/utils";
import ArticleEmailModal from "../components/ArticleEmailModal"; // <-- Import the modal
import AgentAuthorRow from "../components/AgentAuthorRow";
import { useAgentDetails } from "../utils/useAgentDetails";

// Utility for formatting date as "May 1 2025"
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// Replace all {{agent}} in a string with the actual agent value
function templateAgentInHtml(html, agent) {
  if (typeof html !== "string") return html;
  return html.replace(/{{\s*agent\s*}}/g, agent);
}

// Inject referrerpolicy="no-referrer" into every <img> in the article body so
// hotlink-protected hosts (Komodo, some Cloudflare-fronted CDNs, etc.) don't
// refuse the request when the browser sends a cross-origin Referer. Has no
// effect on images that already declare a referrerpolicy.
function addImgReferrerPolicy(html) {
  if (typeof html !== "string") return html;
  return html.replace(/<img\b([^>]*)>/gi, (match, attrs) => {
    if (/\breferrerpolicy\s*=/i.test(attrs)) return match;
    return `<img referrerpolicy="no-referrer"${attrs}>`;
  });
}

// Estimate read time: 1 min per 250 words, minimum 1 min
function estimateReadTime(content) {
  if (!content) return 1;
  const text = content.replace(/<[^>]+>/g, " "); // strip HTML tags
  const wordCount = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / 250));
}

const NewsArticlePage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);

  const agent = searchParams.get("agent") || "";
  const noPopup = searchParams.has("nopopup");
  const { agentDetails } = useAgentDetails();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll depth tracking (25%, 50%, 75%)
  const scrollMilestonesRef = useRef(new Set());
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = (scrollTop / docHeight) * 100;
      [25, 50, 75].forEach(milestone => {
        if (pct >= milestone && !scrollMilestonesRef.current.has(milestone)) {
          scrollMilestonesRef.current.add(milestone);
          trackEvent('scroll_depth', { percent: milestone });
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/article`,
          {
            slug: slug,
            agent_slug: agent,
          },
          {
            "Content-Type": "application/json",
          }
        );
        const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
        setArticle(data);
      } catch (err) {
        setError("Error loading article.");
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, agent]);

  // SEO/meta tags
  useEffect(() => {
    if (!article) return;
    const metaDescription =
      article.description ||
      (article.content ? article.content.replace(/<[^>]+>/g, " ").slice(0, 160) : "");
    const canonicalUrl = window.location.origin + `/news/${slug}?agent=${agent}`;

    // Description
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = "description";
      document.head.appendChild(descTag);
    }
    descTag.content = metaDescription;

    // Open Graph
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = article.title;

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    ogDesc.content = metaDescription;

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = canonicalUrl;

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute("property", "og:image");
      document.head.appendChild(ogImage);
    }
    ogImage.content = article.cover_image;

    // Twitter Card
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.name = "twitter:card";
      document.head.appendChild(twitterCard);
    }
    twitterCard.content = "summary_large_image";

    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.name = "twitter:title";
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.content = article.title;

    let twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDesc) {
      twitterDesc = document.createElement('meta');
      twitterDesc.name = "twitter:description";
      document.head.appendChild(twitterDesc);
    }
    twitterDesc.content = metaDescription;

    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.name = "twitter:image";
      document.head.appendChild(twitterImage);
    }
    twitterImage.content = article.cover_image;

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // Set document title
    document.title = article.title;
  }, [article, slug, agent]);

  // Show modal when user scrolls halfway through the article
  useEffect(() => {
    if (!article || showEmailModal || modalDismissed || !agent || agent === "home-price" || noPopup) return;

    const handleScroll = () => {
      const screenHeight = window.innerHeight || document.documentElement.clientHeight;
      if ((window.scrollY || window.pageYOffset) >= screenHeight) {
        setShowEmailModal(true);
        window.removeEventListener("scroll", handleScroll); // Only trigger once
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [article, showEmailModal, modalDismissed, agent, noPopup]);

  const containerStyle = {
    maxWidth: 800,
    margin: "104px auto 32px auto",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
    padding: "0 48px 48px 48px",
    overflowX: "clip",
  };

  const coverStyle = {
    width: "100%",
    height: "240px",
    objectFit: "cover",
    display: "block",
    marginBottom: 24,
  };

  const extraStyles = `
    @media (max-width: 900px) {
      .article-container {
        padding-left: 32px !important;
        padding-right: 32px !important;
      }
      .sticky-agent-row {
        margin-left: -32px !important;
        margin-right: -32px !important;
        padding-left: 32px !important;
        padding-right: 32px !important;
      }
    }
    @media (max-width: 600px) {
      .article-container {
        padding-left: 16px !important;
        padding-right: 16px !important;
      }
      .article-cover {
        height: 160px !important;
      }
      .sticky-agent-row {
        margin-left: -16px !important;
        margin-right: -16px !important;
        padding-left: 16px !important;
        padding-right: 16px !important;
      }
    }
    .author-row {
      display: flex;
      align-items: center;
      margin-bottom: 24px;
      margin-top: 24px;
    }
    .author-img {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid #eee;
      margin-right: 16px;
      flex-shrink: 0;
    }
    .author-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .author-name {
      font-weight: 600;
      font-size: 1.1rem;
      margin-bottom: 2px;
      text-align: left;
    }
    .author-title {
      color: #888;
      font-weight: 400;
      font-size: 1rem;
      text-align: left;
    }
    .meta-row {
      display: flex;
      align-items: center;
      color: #888;
      font-size: 14px;
      margin-bottom: 16px;
      gap: 8px;
      text-align: left;
      justify-content: flex-start;
    }
    .meta-dot {
      margin: 0 8px;
      font-size: 18px;
      line-height: 1;
    }
    .author-divider {
      border: none;
      border-top: 1px solid #a1a1a1;
      margin: 16px 0 24px 0;
      width: 100%;
    }
    .article-body-content :is(h1, h2, h3, h4, h5, h6) {
      margin-top: 2rem;
      margin-bottom: 1rem;
      font-weight: 600;
    }
    .article-body-content p {
      margin-bottom: 1rem;
      line-height: 1.7;
    }
    .article-body-content ul, .article-body-content ol {
      margin-bottom: 1rem;
      padding-left: 1.5rem;
    }
    .article-body-content li {
      margin-bottom: 0.5rem;
    }
    .article-body-content a {
      color: #0070f3;
      text-decoration: underline;
      word-break: break-all;
    }
    .article-body-content img {
      max-width: 100%;
      height: auto;
    }
    .article-body-content table,
    .article-body-content pre,
    .article-body-content iframe {
      max-width: 100%;
      overflow-x: auto;
    }
  `;

  if (loading) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2>Article not found.</h2>
        <p>
          Sorry, we couldn't find the article you're looking for.
        </p>
      </div>
    );
  }

  const readTime = estimateReadTime(article.content);

  return (
    <>
      <style>{extraStyles}</style>
      <article
        className="article-container"
        style={containerStyle}
        itemScope
        itemType="http://schema.org/Article"
      >
        {article.cover_image && (
          <img
            src={article.cover_image}
            alt={article.title}
            className="article-cover"
            style={coverStyle}
            itemProp="image"
            referrerPolicy="no-referrer"
          />
        )}
        <header>
          <div className="meta-row">
            {article.publish_date && (
              <time
                dateTime={article.publish_date}
                itemProp="datePublished"
              >
                {formatDate(article.publish_date)}
              </time>
            )}
            <span className="meta-dot">&middot;</span>
            <span>{readTime} min read</span>
          </div>
          <h1 itemProp="headline" style={{ marginBottom: 8 }}>{article.title}</h1>
        </header>
        {agentDetails && (
          <div className="sticky-agent-row" style={{ position: 'sticky', top: isMobile ? 56 : 56, zIndex: 1020, backgroundColor: 'white', margin: '0 -48px', padding: '10px 48px 0 48px' }}>
              <AgentAuthorRow agentDetails={agentDetails} align="left" onReportClick={() => setShowEmailModal(true)} authorOverride={article ? { name: article.author, image: article.author_image, title: article.author_title, agency: article.agency_name } : null} />
          </div>
        )}
        <meta itemProp="description" content={article.description || ""} />
        <section itemProp="articleBody">
          <div
            className="article-body-content"
            dangerouslySetInnerHTML={{
              __html: addImgReferrerPolicy(templateAgentInHtml(article.content, agent))
            }}
          />
        </section>
        {/* {article.sources && (
          <section style={{ fontSize: 14, color: "#666", marginTop: 24 }}>
            <strong>Sources:</strong> {article.sources}
          </section>
        )} */}
        {article.cta_link && (
          <section style={{ marginTop: "2rem" }}>
            <Link to={`/property-search?agent=${agent}`} target="_blank" rel="noopener noreferrer" style={{
              color: "white",
              backgroundColor: "#000000",
              padding: "10px 20px",
              borderRadius: 6,
              textDecoration: "none",
              fontWeight: 500,
              fontSize: 16,
              display: "inline-block"
            }}>
              Download your free property report
            </Link>
          </section>
        )}
      </article>
      <ArticleEmailModal
        show={showEmailModal}
        onHide={() => {
          setShowEmailModal(false);
          setModalDismissed(true);
        }}
        articleSlug={slug}
      />
    </>
  );
};

export default NewsArticlePage;